import type { Payout } from '../types/payout';

/**
 * Calculate total value of payouts (sum of totalValue field)
 */
export const sumPayoutValues = (payouts: Payout[]): number => {
  return payouts.reduce((sum, payout) => sum + (payout.totalValue || 0), 0);
};

/**
 * Calculate total value for payouts with type-based logic
 * Handles special cases: vente (negative), swap (both sides)
 */
export const sumPayoutValuesWithTypeLogic = (payouts: Payout[]): number => {
  return payouts.reduce((sum, p) => {
    if (p.type === 'swap') {
      return sum + (p.sentTotalValue || 0) + (p.receivedTotalValue || 0);
    }
    if (p.type === 'vente') {
      return sum - (p.totalValue || 0); // Sales are negative
    }
    return sum + (p.totalValue || 0);
  }, 0);
};

/**
 * Calculate total BNC amount from mining payouts
 */
export const calculateTotalBNCAmount = (payouts: Payout[], bncRate: number = 0.34): number => {
  return payouts.reduce((total, p) => {
    // Only count payout and stacking types for BNC
    return total + (p.type === 'payout' || p.type === 'stacking' ? (p.totalValue || 0) * bncRate : 0);
  }, 0);
};

/**
 * Calculate average purchase price
 */
export const calculateAveragePrice = (
  totalValueAtPurchase: number,
  totalAmount: number
): number => {
  return totalAmount > 0 ? totalValueAtPurchase / totalAmount : 0;
};

/**
 * Calculate total current value: amount × current price
 */
export const calculateCurrentValue = (amount: number, currentPrice: number): number => {
  return amount * currentPrice;
};

/**
 * Calculate profit/loss
 */
export const calculateProfitLoss = (currentValue: number, valueAtPurchase: number): number => {
  return currentValue - valueAtPurchase;
};

/**
 * Calculate profit/loss percentage
 */
export const calculateProfitLossPercentage = (
  currentValue: number,
  valueAtPurchase: number
): number => {
  return valueAtPurchase > 0 ? ((currentValue - valueAtPurchase) / valueAtPurchase) * 100 : 0;
};

/**
 * Calculate average payout value across transactions
 */
export const calculateAveragePayoutValue = (payouts: Payout[]): number => {
  if (payouts.length === 0) return 0;
  return sumPayoutValues(payouts) / payouts.length;
};

/**
 * Calculate total amounts across payouts
 */
export const calculateTotalAmount = (payouts: Payout[]): number => {
  return payouts.reduce((sum, p) => sum + (p.amount || 0), 0);
};

/**
 * Calculate weighted average payout value (accounts for different amounts)
 */
export const calculateWeightedAverageValue = (payouts: Payout[]): number => {
  const totalAmount = calculateTotalAmount(payouts);
  if (totalAmount === 0) return 0;

  const weightedSum = payouts.reduce((sum, p) => {
    return sum + ((p.amount || 0) * (p.unitPrice || 0));
  }, 0);

  return weightedSum / totalAmount;
};

/**
 * Calculate total portfolio value (all tokens at current prices)
 */
export const calculatePortfolioValue = (
  holdings: Map<string, { amount: number; currentPrice: number }>
): number => {
  let totalValue = 0;
  holdings.forEach(({ amount, currentPrice }) => {
    totalValue += calculateCurrentValue(amount, currentPrice);
  });
  return totalValue;
};

/**
 * Calculate acquisition cost proportion for a disposal
 */
export const calculateProportionalCost = (
  soldAmount: number,
  totalAmount: number,
  totalCost: number
): number => {
  if (totalAmount <= 0) return 0;
  const ratio = soldAmount / totalAmount;
  return totalCost * ratio;
};

/**
 * Calculate count of payouts in a given period
 */
export const countPayoutsInPeriod = (payouts: Payout[]): number => {
  return payouts.length;
};

/**
 * Calculate average payouts per month
 */
export const calculateAveragePayoutsPerMonth = (payouts: Payout[]): number => {
  if (payouts.length === 0) return 0;

  const monthsSet = new Set<string>();
  payouts.forEach((p) => {
    const date = new Date(p.date);
    const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
    monthsSet.add(monthKey);
  });

  return payouts.length / monthsSet.size;
};
